import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { kycServices, userServices } from '../../../services'
import { showBanner } from '../../../global-state/banner-slice'
import { CBLogoV2 } from '../../../lib/components/Icon'
import { captureEvent } from '../../../lib/utils/posthogUtils/usePostHog'
import { EVENTS } from '../../../lib/utils/constants'
import { updateConfiguration } from '../../../global-state/investor-slice'
import { resolveRiskProfilerRequired } from '../../../lib/utils/onboarding'
import { resolvePostOnboardingDestination } from '../../../lib/utils/post-login'
import { ACCEPT_NDA_ROUTE } from '../../../lib/utils/campaign-signup-redirect'
import { EngagementEvents, logCrmEvent } from '../../../lib/utils/crmEvents'
import {
  buildAgreedDeclarations,
  buildRiskProfilePatchBody,
  getRiskProfilerAnswersFromStorage,
  isQuestionAnswered,
  RISK_PROFILER_QUESTIONS,
  RiskProfilerAnswers,
  saveRiskProfilerAnswersInStorage,
  setRiskProfilerCompletedInStorage,
  toggleRiskAnswer,
} from '../../../lib/utils/risk-profiler'
import { RiskProfilerPresenter } from './risk-profiler-presenter'
import '../basic-details/basic-details.scss'
import './risk-profiler.scss'

const NEXT_STEP_AFTER_RISK_PROFILER = '/profile/kyc'

const RiskProfilerContainer = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const {
    authToken,
    userId,
    basicDetailsCompleted,
    riskProfileCompleted,
    riskProfilerRequired,
    acceptedNDA,
    kycStatus,
  } = useSelector((state: RootState) => state.investor)
  const profilerRequired = resolveRiskProfilerRequired({
    riskProfilerRequired,
    riskProfileCompleted,
  })
  const [currentStep, setCurrentStep] = useState(0)
  const [answers, setAnswers] = useState<RiskProfilerAnswers>(() =>
    getRiskProfilerAnswersFromStorage(userId),
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [acceptedConfidentiality, setAcceptedConfidentiality] = useState(false)
  const [acceptedNoOffer, setAcceptedNoOffer] = useState(false)
  const [acceptedKycDocuments, setAcceptedKycDocuments] = useState(false)
  const [acceptedAdverseMedia, setAcceptedAdverseMedia] = useState(false)
  const advanceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const redirectIfNeeded = useCallback(() => {
    if (!userId || isSubmitting) return
    if (!profilerRequired) {
      navigate(resolvePostOnboardingDestination(acceptedNDA), { replace: true })
      return
    }
    if (!basicDetailsCompleted) {
      navigate('/onboarding/basic-details', { replace: true })
      return
    }
  }, [navigate, acceptedNDA, basicDetailsCompleted, isSubmitting, profilerRequired, userId])

  useEffect(() => {
    if (!authToken) {
      navigate('/login', { replace: true })
      return
    }
    redirectIfNeeded()
  }, [authToken, navigate, redirectIfNeeded])

  useEffect(() => {
    return () => {
      if (advanceTimeoutRef.current) clearTimeout(advanceTimeoutRef.current)
    }
  }, [])

  const handleSelectOption = (questionId: string, optionId: string) => {
    const question = RISK_PROFILER_QUESTIONS.find((item) => item.id === questionId)
    if (!question) return

    setAnswers((prev) => {
      const next = toggleRiskAnswer(prev, question, optionId)
      saveRiskProfilerAnswersInStorage(userId, next)
      return next
    })

    if (question.multiSelect) return

    const isLastStep = currentStep >= RISK_PROFILER_QUESTIONS.length - 1
    if (isLastStep) return

    if (advanceTimeoutRef.current) clearTimeout(advanceTimeoutRef.current)
    advanceTimeoutRef.current = setTimeout(() => {
      setCurrentStep((step) => Math.min(RISK_PROFILER_QUESTIONS.length - 1, step + 1))
    }, 250)
  }

  const handleAcceptanceChange = (
    field:
      | 'acceptedConfidentiality'
      | 'acceptedNoOffer'
      | 'acceptedKycDocuments'
      | 'acceptedAdverseMedia',
    value: boolean,
  ) => {
    if (field === 'acceptedConfidentiality') setAcceptedConfidentiality(value)
    if (field === 'acceptedNoOffer') setAcceptedNoOffer(value)
    if (field === 'acceptedKycDocuments') setAcceptedKycDocuments(value)
    if (field === 'acceptedAdverseMedia') setAcceptedAdverseMedia(value)
  }

  const handleBack = () => {
    if (advanceTimeoutRef.current) {
      clearTimeout(advanceTimeoutRef.current)
      advanceTimeoutRef.current = null
    }
    setCurrentStep((step) => Math.max(0, step - 1))
  }

  const handleNext = () => {
    const question = RISK_PROFILER_QUESTIONS[currentStep]
    if (!isQuestionAnswered(question, answers)) return
    setCurrentStep((step) => Math.min(RISK_PROFILER_QUESTIONS.length - 1, step + 1))
  }

  const handleSubmit = async () => {
    if (!authToken || !userId) return

    const allAnswered = RISK_PROFILER_QUESTIONS.every((q) => isQuestionAnswered(q, answers))
    if (!allAnswered) {
      dispatch(
        showBanner({
          text: 'Please answer all questions before continuing.',
          variant: 'error-banner',
        }),
      )
      return
    }

    if (
      !acceptedConfidentiality ||
      !acceptedNoOffer ||
      !acceptedKycDocuments ||
      !acceptedAdverseMedia
    ) {
      dispatch(
        showBanner({
          text: 'Please accept all statements before continuing.',
          variant: 'error-banner',
        }),
      )
      return
    }

    setIsSubmitting(true)

    const declarations = buildAgreedDeclarations()
    const riskResponse = await userServices.saveRiskProfile_v1(
      answers,
      { Authorization: authToken },
      declarations,
    )

    if (riskResponse?.status !== 200) {
      setIsSubmitting(false)
      dispatch(
        showBanner({
          text: riskResponse?.data?.message || 'Something went wrong. Please try again.',
          variant: 'error-banner',
        }),
      )
      return
    }

    dispatch(updateConfiguration({ name: 'riskProfileCompleted', value: true }))
    dispatch(updateConfiguration({ name: 'riskProfilerRequired', value: false }))
    setRiskProfilerCompletedInStorage(userId, true)
    saveRiskProfilerAnswersInStorage(userId, answers)
    const { riskBand } = buildRiskProfilePatchBody(answers, declarations).risk_profile_answers
    captureEvent(EVENTS.COMPLETED_RISK_PROFILER, { userId, riskBand })

    const ndaAlreadyAccepted = acceptedNDA || kycStatus === 'APPROVED'
    if (ndaAlreadyAccepted) {
      setIsSubmitting(false)
      dispatch(
        showBanner({
          text: 'Your risk profile has been saved. Continue to the Client Agreement.',
          variant: 'success-banner',
        }),
      )
      navigate(NEXT_STEP_AFTER_RISK_PROFILER, { replace: true })
      return
    }

    const ndaResponse = await kycServices.acceptTNCAndNDA_v1(
      { acceptedTNC: true, acceptedNDA: true },
      { Authorization: authToken },
    )
    setIsSubmitting(false)

    if (ndaResponse?.status === 200) {
      dispatch(
        updateConfiguration({
          name: 'acceptedNDA',
          value: ndaResponse.data.is_nda_accepted ?? true,
        }),
      )
      if (ndaResponse.data?.status) {
        dispatch(
          updateConfiguration({
            name: 'kycStatus',
            value: ndaResponse.data.status,
          }),
        )
      }
      logCrmEvent(EngagementEvents.ACCEPTED_CONFIDENTIALITY_AGREEMENT, {
        Authorization: authToken,
      })
      dispatch(
        showBanner({
          text: 'Your risk profile has been saved. Continue to the Client Agreement.',
          variant: 'success-banner',
        }),
      )
      navigate(NEXT_STEP_AFTER_RISK_PROFILER, { replace: true })
      return
    }

    dispatch(
      showBanner({
        text: 'Risk profile saved. Please accept the Confidentiality Agreement to continue.',
        variant: 'error-banner',
      }),
    )
    navigate(ACCEPT_NDA_ROUTE, { replace: true })
  }

  if (!authToken) return null

  if (!profilerRequired && !isSubmitting) {
    return <Navigate to={resolvePostOnboardingDestination(acceptedNDA)} replace />
  }

  return (
    <div className='basic-details-page risk-profiler-page' data-testid='risk-profiler-page'>
      <header className='basic-details-page__banner'>
        <Link to='/home' className='basic-details-page__logo-link' aria-label='Go to home'>
          <CBLogoV2 width='140' height='33' />
        </Link>
      </header>

      <main className='basic-details-page__form-section' aria-label='Onboarding'>
        <div className='basic-details-page__form-card'>
          <header className='basic-details-page__intro'>
            <h1 className='basic-details-page__intro-title'>Let us know you better!</h1>
          </header>
          <RiskProfilerPresenter
            currentStep={currentStep}
            answers={answers}
            onSelectOption={handleSelectOption}
            onBack={handleBack}
            onNext={handleNext}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            acceptedConfidentiality={acceptedConfidentiality}
            acceptedNoOffer={acceptedNoOffer}
            acceptedKycDocuments={acceptedKycDocuments}
            acceptedAdverseMedia={acceptedAdverseMedia}
            onAcceptanceChange={handleAcceptanceChange}
          />
        </div>
      </main>
    </div>
  )
}

export { RiskProfilerContainer }
